import React, { useState } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Alert,
} from "react-bootstrap";
import InputMask from "react-input-mask";
import moment from "moment-timezone";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";

import { useFormData } from "../../../hooks/useFormData";
import { useApi } from "../../../hooks/useApi";
import ServicesList from "../../common/ServicesList";
import AdminCustomerPaymentForm from "./AdminCustomerPaymentForm";

const RELATIONSHIPS = [
  { label: "Dad", value: "dad" },
  { label: "Mom", value: "mom" },
  { label: "Grandmother", value: "grandmother" },
  { label: "Grandfather", value: "grandfather" },
  { label: "Other family member", value: "other" },
  { label: "Neighbor", value: "neighbor" },
  { label: "Friend", value: "friend" },
];

const servicesToDefaultServices = (services = {}) =>
  Object.keys(services).map((serviceId) => ({
    service: serviceId,
    options: Object.keys(services[serviceId] || {}).map((key) => ({
      key,
      value: services[serviceId][key],
    })),
  }));

const AdminCreateCustomerModal = ({ show, onHide }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState("form");
  const [createdUserId, setCreatedUserId] = useState(null);
  const [includeOtherProfile, setIncludeOtherProfile] = useState(false);
  const [formError, setFormError] = useState(null);

  const { formData, onChange, setData } = useFormData({
    name: "",
    email: "",
    mobile: "",
    street1: "",
    street2: "",
    city: "",
    state: "TN",
    postal: "",
    marketingEmail: true,
    marketingSms: true,
    sendWelcome: true,
    selfServices: {},
    otherName: "",
    otherRelationship: "dad",
    otherPreferredName: "",
    otherEmail: "",
    otherPhone: "",
    otherStreet1: "",
    otherStreet2: "",
    otherCity: "",
    otherState: "TN",
    otherZip: "",
    otherServices: {},
  });

  const { isLoading: isCreating, call: createCustomer } = useApi({
    onSuccess: (res) => {
      setCreatedUserId(res._id);
      setStep("payment");
      queryClient.invalidateQueries("admin-users");
      toast.success("Customer created. Add a payment method to finish.");
    },
    onError: (err) => {
      setFormError(err.message);
      toast.error(err.message);
    },
  });

  const resetAndClose = () => {
    const userId = createdUserId;
    setStep("form");
    setCreatedUserId(null);
    setIncludeOtherProfile(false);
    setFormError(null);
    setData({
      name: "",
      email: "",
      mobile: "",
      street1: "",
      street2: "",
      city: "",
      state: "TN",
      postal: "",
      marketingEmail: true,
      marketingSms: true,
      sendWelcome: true,
      selfServices: {},
      otherName: "",
      otherRelationship: "dad",
      otherPreferredName: "",
      otherEmail: "",
      otherPhone: "",
      otherStreet1: "",
      otherStreet2: "",
      otherCity: "",
      otherState: "TN",
      otherZip: "",
      otherServices: {},
    });
    onHide();
    if (userId) {
      navigate(`/admin/users/${userId}`);
    }
  };

  const onPaymentComplete = () => {
    toast.success("Payment method saved.");
    queryClient.invalidateQueries("admin-users");
    resetAndClose();
  };

  const buildPayload = () => {
    const payload = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      sendWelcome: !!formData.sendWelcome,
      address: {
        street1: formData.street1.trim(),
        street2: formData.street2.trim() || null,
        city: formData.city.trim(),
        state: formData.state,
        postal: formData.postal.trim(),
      },
      settings: {
        mobile: formData.mobile || null,
        timezone: moment.tz.guess(),
        marketingTerms: {
          email: !!formData.marketingEmail,
          sms: !!formData.marketingSms,
        },
      },
      selfDefaultServices: servicesToDefaultServices(formData.selfServices),
    };

    if (includeOtherProfile) {
      payload.otherProfile = {
        name: formData.otherName.trim(),
        relationship: formData.otherRelationship,
        preferredName: formData.otherPreferredName || null,
        email: formData.otherEmail || null,
        phone: formData.otherPhone || null,
        phoneType: "Mobile",
        address: {
          street1: formData.otherStreet1.trim() || formData.street1.trim(),
          street2: formData.otherStreet2.trim() || null,
          city: formData.otherCity.trim() || formData.city.trim(),
          state: formData.otherState || formData.state,
          zip: formData.otherZip.trim() || formData.postal.trim(),
        },
        defaultServices: servicesToDefaultServices(formData.otherServices),
      };
    }

    return payload;
  };

  const onSubmitForm = (e) => {
    e.preventDefault();
    setFormError(null);

    if (!/^[\w]+\s+[\w]+/gi.test(formData.name.trim())) {
      setFormError("Please enter a full name (at least two words).");
      return;
    }

    if (formData.mobile && !/\d{3}-\d{3}-\d{4}/.test(formData.mobile)) {
      setFormError("Invalid phone number format.");
      return;
    }

    if (includeOtherProfile) {
      if (!formData.otherName.trim()) {
        setFormError("Other profile name is required.");
        return;
      }
      if (!formData.otherPhone || !/\d{3}-\d{3}-\d{4}/.test(formData.otherPhone)) {
        setFormError(
          "Mobile is required for the other profile (format 999-999-9999)."
        );
        return;
      }
    }

    createCustomer("/admin/users/customers", "post", undefined, buildPayload());
  };

  const copyAddressToOther = () => {
    setData({
      otherStreet1: formData.street1,
      otherStreet2: formData.street2,
      otherCity: formData.city,
      otherState: formData.state,
      otherZip: formData.postal,
    });
  };

  const renderAddressFields = (prefix, required = true) => {
    const street1 = prefix ? `${prefix}Street1` : "street1";
    const street2 = prefix ? `${prefix}Street2` : "street2";
    const city = prefix ? `${prefix}City` : "city";
    const state = prefix ? `${prefix}State` : "state";
    const zip = prefix ? `${prefix}Zip` : "postal";
    const zipName = prefix ? zip : "postal";

    return (
      <>
        <Form.Group className="mb-3">
          <Form.Label>Street</Form.Label>
          <Form.Control
            name={street1}
            value={formData[street1]}
            onChange={onChange}
            required={required}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Street 2</Form.Label>
          <Form.Control
            name={street2}
            value={formData[street2]}
            onChange={onChange}
          />
        </Form.Group>
        <Row>
          <Col md={5}>
            <Form.Group className="mb-3">
              <Form.Label>City</Form.Label>
              <Form.Control
                name={city}
                value={formData[city]}
                onChange={onChange}
                required={required}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>State</Form.Label>
              <Form.Select
                name={state}
                value={formData[state]}
                onChange={onChange}
                required={required}
              >
                <option value="TN">Tennessee</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Zip</Form.Label>
              <Form.Control
                name={zipName}
                value={formData[zipName]}
                onChange={onChange}
                required={required}
                maxLength={5}
              />
            </Form.Group>
          </Col>
        </Row>
      </>
    );
  };

  const requestClose = () => {
    // Avoid racing create onSuccess: closing mid-request left step="payment"
    // after reset, so the next open showed the prior customer's payment form.
    if (isCreating) {
      return;
    }
    resetAndClose();
  };

  return (
    <Modal show={show} onHide={requestClose} size="lg" backdrop="static">
      <Modal.Header closeButton={!isCreating}>
        <Modal.Title>
          {step === "form" ? "Add Customer" : "Add Payment Method"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {step === "payment" && createdUserId ? (
          <>
            <p className="text-muted">
              A default payment method is required before this customer can be
              booked.
            </p>
            <AdminCustomerPaymentForm
              userId={createdUserId}
              buttonLabel="Save & Finish"
              onComplete={onPaymentComplete}
            />
          </>
        ) : (
          <Form onSubmit={onSubmitForm}>
            {formError ? <Alert variant="danger">{formError}</Alert> : null}

            <h5 className="mb-3">Account</h5>
            <Form.Group className="mb-3">
              <Form.Label>Full name</Form.Label>
              <Form.Control
                name="name"
                value={formData.name}
                onChange={onChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={onChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Mobile (optional)</Form.Label>
              <InputMask
                mask="999-999-9999"
                className="form-control"
                name="mobile"
                value={formData.mobile}
                onChange={onChange}
              />
            </Form.Group>

            <h6 className="mt-2">Address</h6>
            {renderAddressFields("")}

            <Form.Group className="mb-2">
              <Form.Check
                type="checkbox"
                name="marketingEmail"
                label="Marketing emails"
                checked={formData.marketingEmail}
                onChange={(e) =>
                  onChange({
                    target: { name: "marketingEmail", value: e.target.checked },
                  })
                }
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Check
                type="checkbox"
                name="marketingSms"
                label="Marketing SMS"
                checked={formData.marketingSms}
                onChange={(e) =>
                  onChange({
                    target: { name: "marketingSms", value: e.target.checked },
                  })
                }
              />
            </Form.Group>
            <Form.Group className="mb-4">
              <Form.Check
                type="checkbox"
                name="sendWelcome"
                label="Send welcome email"
                checked={formData.sendWelcome}
                onChange={(e) =>
                  onChange({
                    target: { name: "sendWelcome", value: e.target.checked },
                  })
                }
              />
            </Form.Group>

            <hr />
            <h5 className="mb-2">Customer profile</h5>
            <p className="text-muted small">
              Created automatically from account name and address.
            </p>
            <ServicesList
              role="customer"
              values={formData.selfServices}
              onChange={(vals) =>
                onChange({ target: { name: "selfServices", value: vals } })
              }
            />

            <hr />
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Add another care recipient profile"
                checked={includeOtherProfile}
                onChange={(e) => {
                  setIncludeOtherProfile(e.target.checked);
                  if (e.target.checked) {
                    copyAddressToOther();
                  }
                }}
              />
            </Form.Group>

            {includeOtherProfile ? (
              <>
                <h5 className="mb-3">Other profile</h5>
                <Form.Group className="mb-3">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    name="otherName"
                    value={formData.otherName}
                    onChange={onChange}
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Relationship</Form.Label>
                  <Form.Select
                    name="otherRelationship"
                    value={formData.otherRelationship}
                    onChange={onChange}
                    required
                  >
                    {RELATIONSHIPS.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Preferred name (optional)</Form.Label>
                  <Form.Control
                    name="otherPreferredName"
                    value={formData.otherPreferredName}
                    onChange={onChange}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Email (optional)</Form.Label>
                  <Form.Control
                    type="email"
                    name="otherEmail"
                    value={formData.otherEmail}
                    onChange={onChange}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Mobile</Form.Label>
                  <InputMask
                    mask="999-999-9999"
                    className="form-control"
                    name="otherPhone"
                    value={formData.otherPhone}
                    onChange={onChange}
                    required
                  />
                </Form.Group>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">Address</h6>
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    onClick={copyAddressToOther}
                  >
                    Copy from account
                  </Button>
                </div>
                {renderAddressFields("other")}
                <ServicesList
                  role="customer"
                  values={formData.otherServices}
                  onChange={(vals) =>
                    onChange({
                      target: { name: "otherServices", value: vals },
                    })
                  }
                />
              </>
            ) : null}

            <div className="d-flex justify-content-end mt-4 gap-2">
              <Button
                type="button"
                variant="outline-secondary"
                onClick={requestClose}
                disabled={isCreating}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={isCreating}>
                {isCreating ? "Creating..." : "Continue to payment"}
              </Button>
            </div>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default AdminCreateCustomerModal;
